import type { FilterNavigation } from '@/features/publicsite/types/FilterNavigation';

/** How long a listing's search waits after the last keystroke before it sends what was typed. */
export const SEARCH_PAUSE_MS = 400;

/**
 * The timing of a public listing's search box, now that it has no "Caută" button: what is typed
 * goes out once the reader pauses, and Enter sends it at once, dropping the pause still running.
 *
 * `typed` is told the query the URL already holds, and sends nothing while the box says the same —
 * so the box catching up with the URL (a shared link, Back) is not itself a search, which on
 * /stiri would also have dropped the page number.
 *
 * What a pause sends refines the search already in the URL, so it takes that search's place in the
 * history (`replace`) instead of leaving a Back step per pause with a half-typed word in it. A
 * search started from nothing is a new entry, so that Back still undoes it rather than leaving the
 * page; Enter is one too.
 */
export function listingSearch(
  send: (q: string, navigation: FilterNavigation) => void,
  pauseMs = SEARCH_PAUSE_MS,
) {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cancel = () => clearTimeout(timer);
  return {
    typed(query: string, current: string) {
      cancel();
      const q = query.trim();
      if (q !== current) timer = setTimeout(() => send(q, { replace: current !== '' }), pauseMs);
    },
    submitted(query: string) {
      cancel();
      send(query.trim(), { replace: false });
    },
    cancel,
  };
}
